import { useEffect, useId, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  acceptForDependent,
  getDependent,
  getDependentAgreements,
  getFamily,
  removeFamilyDependent,
} from '../lib/api.js';
import {
  FAMILY_PATH,
  acceptForLabel,
  initialOfName,
  nameOf,
  type DependentAgreement,
  type DependentView,
} from '../lib/family.js';
import { formatDay } from '../lib/subscriptions.js';
import { Markdown } from '../lib/markdown.js';
import { Modal } from '../components/Modal.js';
import { Note, Problem } from '../components/form.js';
import { Picture } from './Connections.js';
import { st } from './Family.js';

/**
 * A dependent, from their guardian's side (RidgeWill/account#118), at
 * `/family/dependents/<userId>`.
 *
 * ── READ-ONLY, EXCEPT TWO THINGS ─────────────────────────────────────
 *
 * Accepting a waiting agreement for them, and removing them from the group.
 * Everything else is a list: connections, the people they block, the apps they
 * use and their settings there, the agreements accepted for them. auth sends
 * only that fixed list (auth#616); this page draws nothing it was not sent.
 * Never shown, because never sent: messages, sign-in activity, support, or who
 * blocked them.
 *
 * ── ACCEPTING FOLLOWS THE AGREEMENT GATE ─────────────────────────────
 *
 * The legal exception in PRODUCT.md: each document's full text sits on the
 * page above its own checkbox, "I agree to the Keyodo Terms for Sam and for
 * myself", or "I have read" for a privacy policy. Do not collapse the text
 * behind a link.
 */
export function Dependent({ userId }: { userId: string }) {
  const [view, setView] = useState<DependentView | null>(null);
  const [agreements, setAgreements] = useState<DependentAgreement[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(false);

  async function load() {
    try {
      const [v, a, f] = await Promise.all([getDependent(userId), getDependentAgreements(userId), getFamily()]);
      setView(v);
      setAgreements(a.agreements ?? []);
      setGroupId(
        (f.groups ?? []).find((g) => g.myRole === 'guardian' && g.dependents.some((d) => d.userId === userId))?.id ??
          null,
      );
      setError(null);
    } catch (e) {
      // 404 for anyone who is not their current guardian, the same as for an id
      // that names nobody: this page cannot and should not tell the two apart.
      if ((e as { status?: number }).status === 404) setMissing(true);
      else setError(e instanceof Error ? e.message : 'Could not load this page.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const back = (
    <a href={FAMILY_PATH} style={{ color: C.brand, fontSize: '.9rem', textDecoration: 'none' }}>
      &larr; Family Group
    </a>
  );

  if (removed) {
    return (
      <section style={{ maxWidth: 640 }}>
        {back}
        <p role="status" style={{ ...st.p, marginTop: '1.25rem' }}>
          {nameOf(view?.person)} is no longer in your Family Group. They have been told.
        </p>
      </section>
    );
  }
  if (missing) {
    return (
      <section style={{ maxWidth: 640 }}>
        {back}
        <h2 style={{ ...st.h2, marginTop: '1.25rem' }}>Not in your Family Group</h2>
        <Note>They may have left or been removed, or the link you followed is out of date.</Note>
      </section>
    );
  }
  if (error && !view) return <Problem>{error}</Problem>;
  if (!view) return <Note>Loading…</Note>;

  const name = nameOf(view.person);
  const waiting = agreements.filter((a) => a.waitingForGuardian);
  const accepted = agreements.filter((a) => a.guardian.accepted || a.self.accepted);

  return (
    <section style={{ maxWidth: 680 }}>
      {back}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '1.25rem 0 0' }}>
        <Picture image={view.person.image} initial={initialOfName(name)} size={64} />
        <div style={{ minWidth: 0 }}>
          <h2 style={{ ...st.h2, margin: 0, overflowWrap: 'anywhere' }}>{name}</h2>
          <Note>In your Family Group since {formatDay(view.joinedAt)}.</Note>
        </div>
      </div>

      {view.guardianAccepts === false ? (
        <Note>{name} is 18 or older and accepts their own agreements.</Note>
      ) : (
        waiting.length > 0 && <WaitingForYou name={name} userId={userId} waiting={waiting} onAccepted={load} />
      )}

      <h3 style={st.h3}>Connections</h3>
      {view.connections.length === 0 ? (
        <Note>Not connected with anyone.</Note>
      ) : (
        <ul style={st.list}>
          {view.connections.map((c) => (
            <PersonLine
              key={c.userId}
              name={nameOf(c)}
              image={c.image}
              detail={
                c.family
                  ? 'Family'
                  : c.connectedSince
                    ? `Connected since ${formatDay(c.connectedSince)}`
                    : null
              }
            />
          ))}
        </ul>
      )}

      <h3 style={st.h3}>Blocked</h3>
      {view.blocks.length === 0 ? (
        <Note>Has not blocked anyone.</Note>
      ) : (
        <ul style={st.list}>
          {view.blocks.map((b) => (
            <PersonLine key={b.userId} name={nameOf(b)} image={b.image} detail={null} />
          ))}
        </ul>
      )}

      <h3 style={st.h3}>Apps</h3>
      {view.apps.length === 0 ? (
        <Note>Has not used a Ridgewill app yet.</Note>
      ) : (
        <ul style={st.list}>
          {view.apps.map((app) => (
            <li key={app.slug} style={{ ...st.row, display: 'block' }}>
              <div style={{ fontWeight: 500 }}>{app.name}</div>
              {app.settings.length === 0 ? (
                <Note>No settings in this app yet.</Note>
              ) : (
                <dl style={{ margin: '.35rem 0 0', display: 'grid', gap: '.2rem' }}>
                  {app.settings.map((s) => (
                    <div key={s.key} style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', fontSize: '.9rem' }}>
                      <dt style={{ color: C.textMid }}>{s.label}:</dt>
                      <dd style={{ margin: 0 }}>{settingValue(s.value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ul>
      )}

      <h3 style={st.h3}>Agreements</h3>
      {accepted.length === 0 ? (
        <Note>None accepted yet.</Note>
      ) : (
        <ul style={st.list}>
          {accepted.map((a) => (
            <li key={a.agreementVersionId} style={{ ...st.row, display: 'block' }}>
              <div style={{ fontWeight: 500 }}>
                {a.name} <span style={{ color: C.textMid, fontWeight: 400, fontSize: '.85rem' }}>Version {a.version}</span>
              </div>
              <Note>
                {a.self.accepted && a.self.acceptedAt
                  ? `${name} accepted ${formatDay(a.self.acceptedAt)}.`
                  : `${name} has not accepted yet.`}{' '}
                {a.guardian.accepted && a.guardian.acceptedAt
                  ? `${a.guardian.byMe ? 'You' : 'A guardian'} accepted ${formatDay(a.guardian.acceptedAt)}.`
                  : ''}
              </Note>
            </li>
          ))}
        </ul>
      )}

      <div style={st.out}>
        <button type="button" onClick={() => setRemoving(true)} style={st.quiet} disabled={!groupId}>
          Remove {name} from the Family Group
        </button>
      </div>

      <RemoveDialog
        open={removing}
        name={name}
        minor={view.guardianAccepts !== false}
        onClose={() => setRemoving(false)}
        onRemove={async () => {
          if (!groupId) return;
          await removeFamilyDependent(groupId, userId);
          setRemoving(false);
          setRemoved(true);
        }}
      />
    </section>
  );
}

function settingValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'On' : 'Off';
  if (v === null || v === undefined) return 'Not set';
  return String(v);
}

function PersonLine({ name, image, detail }: { name: string; image: string | null; detail: string | null }) {
  return (
    <li style={st.row}>
      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', minWidth: 0 }}>
        <Picture image={image} initial={initialOfName(name)} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{name}</div>
          {detail && <Note>{detail}</Note>}
        </div>
      </div>
    </li>
  );
}

/**
 * "Waiting for you": the documents the dependent owes that need this
 * guardian's half, each in full above its own checkbox.
 */
function WaitingForYou({
  name,
  userId,
  waiting,
  onAccepted,
}: {
  name: string;
  userId: string;
  waiting: DependentAgreement[];
  onAccepted: () => void;
}) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const all = waiting.every((a) => checked[a.agreementVersionId]);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      for (const a of waiting) await acceptForDependent(userId, a.agreementVersionId);
      onAccepted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That could not be recorded. Try again.');
      onAccepted();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...st.card, marginTop: '1.5rem', background: 'transparent' }}>
      <h3 style={{ ...st.h3, marginTop: 0 }}>Waiting for you</h3>
      <p style={st.p}>
        {name} needs you to accept {waiting.length === 1 ? 'this document' : 'these documents'} too. Read each one
        below.
      </p>
      {waiting.map((a) => (
        <Doc
          key={a.agreementVersionId}
          agreement={a}
          label={acceptForLabel(a, name)}
          checked={Boolean(checked[a.agreementVersionId])}
          onChange={(v) => setChecked((c) => ({ ...c, [a.agreementVersionId]: v }))}
        />
      ))}
      {error && <Problem>{error}</Problem>}
      <div style={st.actions}>
        <button type="button" disabled={!all || busy} onClick={() => void accept()} style={st.primary(!all || busy)}>
          {busy ? 'Recording…' : `Accept for ${name}`}
        </button>
      </div>
    </div>
  );
}

function Doc({
  agreement: a,
  label,
  checked,
  onChange,
}: {
  agreement: DependentAgreement;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <section style={{ marginTop: '1rem' }} aria-labelledby={`${id}-name`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <h4 id={`${id}-name`} style={{ margin: 0, fontSize: '1rem' }}>
          {a.name}
        </h4>
        <span style={{ color: C.textMid, fontSize: '.85rem' }}>Version {a.version}</span>
      </div>
      {a.summary && (
        <p style={{ ...st.p, margin: '0 0 .6rem' }}>
          <strong style={{ color: C.text }}>What&rsquo;s changed:</strong> {a.summary}
        </p>
      )}
      <div
        className="rw-agreement"
        tabIndex={0}
        role="region"
        aria-label={`${a.name}, full text`}
        style={{
          overflowY: 'auto',
          background: C.page,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          fontSize: '.92rem',
          color: C.text,
          overscrollBehavior: 'contain',
        }}
      >
        {a.contentFormat === 'markdown' ? (
          <Markdown source={a.content} />
        ) : (
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{a.content}</p>
        )}
      </div>
      <label htmlFor={`${id}-ok`} style={st.check}>
        <input id={`${id}-ok`} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rw-check" />
        <span>{label}</span>
      </label>
    </section>
  );
}

function RemoveDialog({
  open,
  name,
  minor,
  onClose,
  onRemove,
}: {
  open: boolean;
  name: string;
  /** Under 18: removal locks what needs a guardian. */
  minor: boolean;
  onClose: () => void;
  onRemove: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  return (
    <Modal open={open} title={`Remove ${name}?`} onClose={onClose}>
      <p style={st.p}>
        {name} will no longer be in your Family Group, and they will be told.{' '}
        {minor
          ? 'Anything in a Ridgewill app that needs a parent or guardian, like borrowing in Keyodo, is locked for them until another Family Group adds them. '
          : ''}
        Agreements you accepted for them stay on record.
      </p>
      {error && <Problem>{error}</Problem>}
      <div style={st.actions}>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            onRemove().then(
              () => setBusy(false),
              (e: unknown) => {
                setBusy(false);
                setError(e instanceof Error ? e.message : `${name} could not be removed.`);
              },
            );
          }}
          style={st.button}
        >
          {busy ? 'Removing…' : `Remove ${name}`}
        </button>
        <button type="button" disabled={busy} onClick={onClose} style={st.button}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
